import * as React from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Close } from "@mui/icons-material";
import { useState } from "react";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import {
  useDeletePackageMutation,
  useGetPackagesQuery,
} from "../../Slices/roomNateApis";
import { useGlobalAuthContext } from "../../context/AuthContext";
import AddPackageModal from "./AddPackageModal";

function PackageCard({ row, onEditClick, onDeleteClick }) {
  const visibilityLabel = row?.iframeVisibility
    ? "iFrame visible"
    : row?.productVisibility
    ? "Product visible"
    : null;

  return (
    <Card
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        {/* Name */}
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{ mb: 1.5, fontSize: "1rem", letterSpacing: 0.2 }}
        >
          {row?.name || "Untitled"}
        </Typography>

        {/* Visibility chip */}
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
          {visibilityLabel ? (
            <Chip label={visibilityLabel} size="small" variant="outlined" />
          ) : (
            <Chip
              label="No visibility set"
              size="small"
              variant="outlined"
              sx={{ color: "text.disabled", borderColor: "divider" }}
            />
          )}
        </Stack>

        {/* Boolean feature chips */}
        <Stack direction="row" flexWrap="wrap" gap={1}>
          <Chip
            label="Allow Magic"
            size="small"
            color={row?.allowMakeMagic ? "success" : "error"}
            variant={row?.allowMakeMagic ? "filled" : "outlined"}
          />
          <Chip
            label="Manual items"
            size="small"
            color={row?.allowManualItems ? "success" : "error"}
            variant={row?.allowManualItems ? "filled" : "outlined"}
          />
        </Stack>
      </CardContent>

      <Divider />

      {/* Actions */}
      <CardActions sx={{ px: 1.5, py: 1, gap: 0.5 }}>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => onEditClick(row)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" color="error" onClick={() => onDeleteClick(row._id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
}

export default function PackagesTable({ productsData }) {
  const { currentUserSessionStorage } = useGlobalAuthContext();

  const [selectedId, setSelectedId] = React.useState();
  const [open, setOpen] = React.useState(false);
  const [successAlert, setSuccessAlert] = React.useState(false);
  const [errorAlert, setErrorAlert] = React.useState(false);
  const [editProduct, setEditProduct] = useState({});

  const { isFetching, refetch } = useGetPackagesQuery(1);
  const [deletePackage, resultStats] = useDeletePackageMutation();

  React.useEffect(() => {
    refetch();
  }, [currentUserSessionStorage, refetch]);

  React.useEffect(() => {
    if (resultStats?.isSuccess) {
      setSuccessAlert(true);
      setTimeout(() => setSuccessAlert(false), 2000);
    }
    if (resultStats?.isError) {
      setErrorAlert(true);
      setTimeout(() => setErrorAlert(false), 2000);
    }
  }, [resultStats]);

  const handleClickOpen = (id) => {
    setSelectedId(id);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleDelete = () => {
    handleClose();
    deletePackage(selectedId);
  };

  const packages = Array.isArray(productsData)
    ? productsData
    : productsData?.items || [];

  return (
    <>
      {successAlert && <SuccessAlert title="Package has been deleted successfully!" />}
      {errorAlert && <ErrorAlert title="Something Went Wrong!" />}

      {/* Header row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: 3,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          Packages{" "}
          {packages.length > 0 && (
            <Typography component="span" variant="body2" color="text.secondary">
              ({packages.length})
            </Typography>
          )}
        </Typography>
        <AddPackageModal editProduct={editProduct} setEditProduct={setEditProduct} />
      </Box>

      {/* Card grid */}
      {isFetching ? (
        <Typography color="text.secondary">Loading...</Typography>
      ) : packages.length === 0 ? (
        <Typography color="text.secondary">No packages found.</Typography>
      ) : (
        <Grid container spacing={2}>
          {packages.map((row, index) => (
            <Grid item xs={12} sm={6} lg={4} key={row._id || index}>
              <PackageCard
                row={row}
                onEditClick={setEditProduct}
                onDeleteClick={handleClickOpen}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        maxWidth="sm"
        fullWidth
        open={open}
        aria-labelledby="delete-pkg-title"
        aria-describedby="delete-pkg-description"
      >
        <DialogTitle id="delete-pkg-title">Confirm the Action?</DialogTitle>
        <Box position="absolute" top={10} right={0}>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
        <DialogContent>
          <DialogContentText id="delete-pkg-description">
            Do you really want to delete this package?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
